/**
 * Класс для математических расчетов гексагональной сетки
 */
export class HexMath {
  constructor(hexSize = 30, orientation = 'flat', canvasWidth = 1200, canvasHeight = 800) {
    this.hexSize = hexSize;
    this.orientation = orientation;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.centerX = canvasWidth / 2;
    this.centerY = canvasHeight / 2;
  }

  /**
   * Установить размеры canvas для центрирования
   * @param {number} width - Ширина canvas
   * @param {number} height - Высота canvas
   */
  setCanvasSize(width, height) {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.centerX = width / 2;
    this.centerY = height / 2;
  }

  /**
   * Получить координаты центра гексагона в пикселях
   * @param {number} q - q координата гекса
   * @param {number} r - r координата гекса
   * @returns {Object} {x, y}
   */
  hexToPixel(q, r) {
    if (this.orientation === 'pointy') {
      const x = this.hexSize * (Math.sqrt(3) * q + Math.sqrt(3) / 2 * r);
      const y = this.hexSize * (3 / 2 * r);
      return { x: x + this.centerX, y: y + this.centerY };
    } else {
      const x = this.hexSize * (3 / 2 * q);
      const y = this.hexSize * (Math.sqrt(3) / 2 * q + Math.sqrt(3) * r);
      return { x: x + this.centerX, y: y + this.centerY };
    }
  }

  /**
   * Получить координаты соседних гексов для flat-top ориентации
   * @param {number} q - q координата текущего гекса
   * @param {number} r - r координата текущего гекса
   * @returns {Array} Массив координат соседей [[q, r], ...]
   */
  getNeighborsFlatTop(q, r) {
    return [
      [q + 1, r],
      [q - 1, r],
      [q, r + 1],
      [q, r - 1],
      [q + 1, r - 1],
      [q - 1, r + 1]
    ];
  }

  /**
   * Рассчитать расстояние между двумя гексами
   * @param {number} q1 - q координата первого гекса
   * @param {number} r1 - r координата первого гекса
   * @param {number} q2 - q координата второго гекса
   * @param {number} r2 - r координата второго гекса
   * @returns {number} Расстояние в клетках
   */
  hexDistance(q1, r1, q2, r2) {
    const s1 = -q1 - r1;
    const s2 = -q2 - r2;
    return (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs(s1 - s2)) / 2;
  }

  /**
   * Получить точки вершин гексагона
   * @param {number} centerX - X координата центра
   * @param {number} centerY - Y координата центра
   * @returns {Array} Массив точек [{x, y}, ...]
   */
  getHexPoints(centerX, centerY) {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = this.orientation === 'pointy'
        ? (Math.PI / 3) * i - Math.PI / 6
        : (Math.PI / 3) * i;

      const px = centerX + this.hexSize * Math.cos(angle);
      const py = centerY + this.hexSize * Math.sin(angle);
      points.push({ x: px, y: py });
    }
    return points;
  }

  /**
   * Получить границы гексагона
   * @param {Array} points - Точки вершин гексагона
   * @returns {Object} {minX, maxX, minY, maxY, width, height}
   */
  getHexBounds(points) {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    for (const point of points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }

    return {
      minX, maxX, minY, maxY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Рассчитать масштаб для спрайта, чтобы вписать в гексагон
   * @param {Object} hexBounds - Границы гексагона
   * @param {Object} spriteSize - Размеры спрайта {width, height}
   * @returns {number} Коэффициент масштабирования
   */
  calculateSpriteScale(hexBounds, spriteSize) {
    const scaleX = hexBounds.width / spriteSize.width;
    const scaleY = hexBounds.height / spriteSize.height;
    return Math.min(scaleX, scaleY) * 0.96;
  }
}